import React from 'react';
import { Helmet } from 'react-helmet-async';
import { generateBreadcrumbSchema } from '../utils/schemaGenerator';

/**
 * SEOHead Component - Gelişmiş SEO ve Schema.org desteği
 * 
 * @param {string} title - Sayfa başlığı
 * @param {string} description - Meta açıklama (max 160 karakter önerilir)
 * @param {string} keywords - Anahtar kelimeler
 * @param {string} canonicalUrl - Canonical URL
 * @param {string} ogImage - Open Graph görseli
 * @param {string} ogType - Open Graph tipi (website, product, article)
 * @param {string} twitterCard - Twitter card tipi
 * @param {Object|Array} structuredData - Schema.org yapısal veri (tek obje veya array)
 * @param {Array} breadcrumbs - Breadcrumb dizisi [{name, path}]
 * @param {Array} preloadImages - Ön yüklenecek görseller
 * @param {Array} preconnectDomains - Preconnect domainler
 * @param {boolean} noIndex - Sayfa indekslenmesin mi?
 * @param {boolean} noFollow - Linkler takip edilmesin mi?
 * @param {string} publishedTime - Yayınlanma tarihi (ISO 8601)
 * @param {string} modifiedTime - Değiştirilme tarihi (ISO 8601)
 */
const SEOHead = ({
  title = "Minela Kids - Çocuk Giyim ve Moda",
  description = "Minela Kids'te en kaliteli çocuk giyim ürünlerini keşfedin. Bebek ve çocuk kıyafetleri, ayakkabılar ve aksesuarlar için güvenilir adres. Ücretsiz kargo ve hızlı teslimat.",
  keywords = "çocuk giyim, minela kids, çocuk kıyafeti, bebek giyim, çocuk mont, çocuk ayakkabı, kız çocuk elbise, erkek çocuk pantolon",
  canonicalUrl,
  ogImage = "/minela_kids_logo.jpg",
  ogType = "website",
  twitterCard = "summary_large_image",
  structuredData,
  breadcrumbs = null,
  preloadImages = [],
  preconnectDomains = [],
  noIndex = false,
  noFollow = false,
  publishedTime = null,
  modifiedTime = null
}) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || "https://minelakids.com";
  const siteName = process.env.REACT_APP_SITE_NAME || "Minela Kids";
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  const fullCanonicalUrl = canonicalUrl || `${siteUrl}${window.location.pathname}`;
  
  // Description uzunluk kontrolü (SEO best practice: max 160 karakter)
  const metaDescription = description.length > 160 
    ? description.substring(0, 157) + '...'
    : description;
  
  // Schema array'i hazırla
  const schemas = [];
  
  // Varolan structuredData'yı ekle (tek obje veya array olabilir)
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      schemas.push(...structuredData);
    } else {
      schemas.push(structuredData);
    }
  }
  
  // Breadcrumb schema'sı ekle
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
    if (breadcrumbSchema) {
      schemas.push(breadcrumbSchema);
    }
  }

  return (
    <Helmet>
      {/* Temel Meta Tag'leri */}
      <html lang="tr" />
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Minela Kids" />
      <meta name="robots" content={`${noIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="tr_TR" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@minelakids" />
      <meta name="twitter:creator" content="@minelakids" />
      
      {/* Mobil Optimizasyon */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="theme-color" content="#8BC34A" />
      
      {/* Performans için Domain Preconnect */}
      {preconnectDomains.map((domain, index) => (
        <link key={`preconnect-${index}`} rel="preconnect" href={domain} />
      ))}
      
      {/* Performans için Resim Ön Yükleme */}
      {preloadImages.map((imageUrl, index) => (
        <link key={`preload-${index}`} rel="preload" as="image" href={imageUrl} />
      ))}
      
      {/* Font Ön Yükleme */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      
      {/* Structured Data (Schema.org JSON-LD) */}
      {schemas.length > 0 && schemas.map((schema, index) => (
        <script 
          key={`schema-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      
      {/* Core Web Vitals için kritik CSS */}
      <style>{`
        /* CLS (Cumulative Layout Shift) optimizasyonu için temel stiller */
        img {
          height: auto;
          max-width: 100%;
        }
        
        /* FID (First Input Delay) optimizasyonu için non-blocking fontlar */
        @font-face {
          font-family: 'Inter';
          font-display: swap;
        }
      `}</style>
    </Helmet>
  );
};

export default SEOHead;